// Red "this spot is not valid" tint of placed items, kept in step with where the
// items actually are. A drop paints it; anything that moves items afterwards
// without a drag (undo/redo, a removed neighbour, a refetch) must re-derive it,
// or an item keeps the tint of a spot it has already left.
import { clearResolvedConflictTints, getFurnitureDepthComponents } from './depthMath';
import { footprintOutOfRoom } from './footprintTolerance';
import { readFurnitureData, type SceneNode, type SceneNodeChild } from './sceneNode';
import { getTileDims } from './tileMath';
import { isPlacedWallItemValid } from './windowPlacement';

export const CONFLICT_TINT = 0xFF4444;

/**
 * Floor items: clear the tint once their conflict is gone (clearResolvedConflictTints).
 * Rugs and wall items: red exactly when their spot is invalid — a rug off the floor,
 * a wall item off the wall or over another one. `skipIds`: items being dragged.
 */
export function refreshPlacementTints(markers: Map<number, SceneNode>, skipIds: ReadonlySet<number> = new Set()): void {
  clearResolvedConflictTints(markers);
  for (const [id, marker] of markers) {
    if (skipIds.has(id)) continue;
    const sp = marker.children[0] as SceneNodeChild | undefined;
    const fData = readFurnitureData(marker, sp);
    if (!sp || !fData) continue;
    const { w, h, isRug, isWindow } = getTileDims(fData.furniture_catalog);
    let invalid: boolean;
    if (isRug) {
      invalid = footprintOutOfRoom(null, getFurnitureDepthComponents(marker, w, h, true), sp.scale.x < 0);
    } else if (isWindow) {
      invalid = !isPlacedWallItemValid(markers, marker, fData, sp);
    } else {
      continue;
    }
    sp.tint = invalid ? CONFLICT_TINT : 0xFFFFFF;
    sp.alpha = invalid ? 0.5 : 1;
  }
}
